import { createContext, ReactNode, useEffect, useReducer } from 'react';
import { OAUTH_DISABLED } from '../config';
import { validateToken } from '../utils/deviseTokenAuth';
import { ActionMap, AuthState, DeviseTokenAuthContextType } from '../@types/auth';
import { CurrentUser } from '~/api/scheme';
import { QueryKeys } from '~/api/queryKeys';
import { useQuery } from 'react-query';
import { UserDataJSONAPI } from '~/sections/user-profile/types';

enum Types {
  Auth = 'AUTHENTICATE',
}

type DeviseTokenAuthPayload = {
  [Types.Auth]: {
    isAuthenticated: boolean;
    isMasquerade: boolean;
    user: UserDataJSONAPI | undefined;
  };
};

type DeviseTokenAuthActions =
  ActionMap<DeviseTokenAuthPayload>[keyof ActionMap<DeviseTokenAuthPayload>];

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  isMasquerade: false,
  user: undefined,
};

const DeviseTokenAuthReducer = (state: AuthState, action: DeviseTokenAuthActions) => {
  if (action.type === 'AUTHENTICATE') {
    const { isAuthenticated, user, isMasquerade } = action.payload;
    return {
      isAuthenticated,
      isInitialized: true,
      isMasquerade,
      user,
    };
  }
  return state;
};

const AuthContext = createContext<DeviseTokenAuthContextType | null>(null);

type AuthProviderProps = {
  children: ReactNode;
};

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(DeviseTokenAuthReducer, initialState);

  const dispatchSuccessfulAuthentication = (user: UserDataJSONAPI, isMasquerade: boolean) => {
    dispatch({
      type: Types.Auth,
      payload: {
        isAuthenticated: true,
        isMasquerade,
        user,
      },
    });
  };

  const dispatchFailedAuthentication = () => {
    dispatch({
      type: Types.Auth,
      payload: {
        isAuthenticated: false,
        isMasquerade: false,
        user: undefined,
      },
    });
  };

  const { data, isLoading } = useQuery(QueryKeys.currentUser(), CurrentUser.show);
  const user = data?.data?.data;
  const isMasquerade = data?.data?.meta?.masquerade;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const initialize = async () => {
      try {
        if (OAUTH_DISABLED) {
          if (user) {
            dispatchSuccessfulAuthentication(user, isMasquerade);
          } else {
            dispatchFailedAuthentication();
          }
          return;
        }

        const isTokenValid = await validateToken();

        if (isTokenValid && user) {
          dispatchSuccessfulAuthentication(user, isMasquerade);
        } else {
          dispatchFailedAuthentication();
        }
      } catch (err) {
        console.error(err);
        dispatchFailedAuthentication();
      }
    };

    initialize();
  }, [user, isMasquerade, isLoading]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'devise-token-auth',
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
